import React, { useState } from 'react';
import {
  CheckCircleIcon,
  XCircleIcon,
  TrashIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const BulkModerationActions = ({ 
  selectedAds, 
  onBulkAccept, 
  onBulkReject, 
  onBulkDelete, 
  onClearSelection,
  isVisible 
}) => {
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  if (!isVisible || selectedAds.length === 0) {
    return null;
  }

  const handleBulkAccept = async () => {
    setIsProcessing(true);
    try {
      await onBulkAccept(selectedAds.map(ad => ad.id));
      onClearSelection();
    } catch (error) {
      console.error('Error in bulk accept:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBulkReject = async () => {
    if (!rejectReason && !customReason.trim()) {
      alert('Пожалуйста, выберите причину отклонения');
      return;
    }

    setIsProcessing(true);
    try {
      const reason = customReason.trim() || rejectReason;
      await onBulkReject(selectedAds.map(ad => ad.id), reason);
      setShowRejectModal(false);
      setRejectReason('');
      setCustomReason('');
      onClearSelection();
    } catch (error) {
      console.error('Error in bulk reject:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(`Вы уверены, что хотите удалить ${selectedAds.length} объявлений? Это действие нельзя отменить.`)) {
      setIsProcessing(true);
      try {
        await onBulkDelete(selectedAds.map(ad => ad.id));
        onClearSelection();
      } catch (error) {
        console.error('Error in bulk delete:', error);
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const rejectReasons = [
    'Спам',
    'Неподходящий контент',
    'Некорректная информация',
    'Дубликат',
    'Нарушение правил',
    'Другая причина'
  ];

  return (
    <>
      {/* Bulk Actions Bar */}
      <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50">
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4 min-w-96">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-sm font-semibold text-blue-600">{selectedAds.length}</span>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">
                  Выбрано {selectedAds.length} объявлений
                </p>
                <p className="text-xs text-gray-500">Выберите действие для всех выбранных объявлений</p>
              </div>
            </div>
            
            <button
              onClick={onClearSelection}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Очистить выбор"
            >
              <XCircleIcon className="w-5 h-5" />
            </button>
          </div>

          <div className="flex items-center space-x-3 mt-4">
            <button
              onClick={handleBulkAccept}
              disabled={isProcessing}
              className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-emerald-400 to-emerald-500 text-emerald-950 font-semibold rounded-lg hover:from-emerald-500 hover:to-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md"
            >
              <CheckCircleIcon className="w-4 h-4" />
              <span>Принять все</span>
            </button>

            <button
              onClick={() => setShowRejectModal(true)}
              disabled={isProcessing}
              className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-rose-400 to-pink-500 text-rose-950 font-semibold rounded-lg hover:from-rose-500 hover:to-pink-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md"
            >
              <XCircleIcon className="w-4 h-4" />
              <span>Отклонить все</span>
            </button>

            <button
              onClick={handleBulkDelete}
              disabled={isProcessing}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <TrashIcon className="w-4 h-4" />
              <span>Удалить все</span>
            </button>
          </div>
        </div>
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Отклонить объявления</h3>
                  <p className="text-sm text-gray-500">
                    Вы собираетесь отклонить {selectedAds.length} объявлений
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Причина отклонения
                  </label>
                  <select
                    value={rejectReason}
                    onChange={(e) => {
                      setRejectReason(e.target.value);
                      if (e.target.value !== 'Другая причина') {
                        setCustomReason('');
                      }
                    }}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  >
                    <option value="">Выберите причину</option>
                    {rejectReasons.map(reason => (
                      <option key={reason} value={reason}>{reason}</option>
                    ))}
                  </select>
                </div>

                {rejectReason === 'Другая причина' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Укажите причину
                    </label>
                    <textarea
                      value={customReason}
                      onChange={(e) => setCustomReason(e.target.value)}
                      placeholder="Введите причину отклонения..."
                      rows={3}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                    />
                  </div>
                )}

                <div className="flex items-center space-x-3 pt-4">
                  <button
                    onClick={() => setShowRejectModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Отмена
                  </button>
                  <button
                    onClick={handleBulkReject}
                    disabled={isProcessing || (!rejectReason && !customReason.trim())}
                    className="flex-1 px-4 py-2 bg-gradient-to-r from-rose-400 to-pink-500 text-rose-950 font-semibold rounded-lg hover:from-rose-500 hover:to-pink-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md"
                  >
                    {isProcessing ? 'Обработка...' : 'Отклонить все'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BulkModerationActions;
